// frontend/modules/customer-notification/views/NotificationHistoryView.tsx
import React, { useEffect } from 'react';
import { Card, Typography, Button, Space, DatePicker } from 'antd';
import { ReloadOutlined, FilterOutlined, HistoryOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { useLocation } from 'react-router-dom';
import NotificationHistoryFilters from '../components/history/NotificationHistoryFilters';
import NotificationHistoryTable from '../components/history/NotificationHistoryTable';
import { useNotificationHistory } from '../hooks/useNotificationHistory';

const { Title } = Typography;

const NotificationHistoryView: React.FC = () => {
    const { dark } = useTheme();
    const location = useLocation();
    
    const { 
        filteredNotifications, 
        isLoading, 
        searchTerm,
        selectedBranch,
        selectedPlatform,
        selectedCategory,
        startDate,
        endDate,
        successStatus,
        
        setSearchTerm,
        setSelectedBranch,
        setSelectedPlatform,
        setSelectedCategory,
        setDateRange,
        setSuccessStatus,
        resetFilters,
        refreshNotifications
    } = useNotificationHistory();
    
    // Get notification ID from URL if present
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const notificationId = searchParams.get('id');
        
        if (notificationId) {
            // If a specific notification ID is provided, we could load that detail view
            // But for now, we'll just refresh the list
            refreshNotifications();
        }
    }, [location, refreshNotifications]);
    
    // Mock data for branches
    const branches = [
        { value: '1', label: 'Main Branch' },
        { value: '2', label: 'North Branch' },
        { value: '3', label: 'South Branch' },
        { value: '4', label: 'East Branch' },
        { value: '5', label: 'West Branch' },
    ];
    
    return (
        <div className="space-y-6">
            <Card
                className={dark ? 'bg-gray-800 text-white' : ''}
                title={
                    <div className="flex justify-between items-center">
                        <Title level={4} className={dark ? 'text-white mb-0' : 'mb-0'}>
                            <Space>
                                <HistoryOutlined />
                                <span>Notification History</span>
                            </Space>
                        </Title>
                        <Space>
                            <Button
                                icon={<FilterOutlined />}
                                onClick={resetFilters}
                            >
                                Reset Filters
                            </Button>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={refreshNotifications}
                                loading={isLoading}
                            >
                                Refresh
                            </Button>
                        </Space>
                    </div>
                }
            >
                <NotificationHistoryFilters
                    searchTerm={searchTerm}
                    selectedBranch={selectedBranch}
                    selectedPlatform={selectedPlatform}
                    selectedCategory={selectedCategory}
                    startDate={startDate}
                    endDate={endDate}
                    successStatus={successStatus}
                    isLoading={isLoading}
                    onSearchChange={setSearchTerm}
                    onBranchChange={setSelectedBranch}
                    onPlatformChange={setSelectedPlatform}
                    onCategoryChange={setSelectedCategory}
                    onDateRangeChange={setDateRange}
                    onSuccessStatusChange={setSuccessStatus}
                    onResetFilters={resetFilters}
                    onRefresh={refreshNotifications}
                    branches={branches}
                />
                
                <NotificationHistoryTable
                    notifications={filteredNotifications}
                    isLoading={isLoading}
                />
            </Card>
        </div>
    );
};

export default NotificationHistoryView;
